import React, { useState, useEffect, useCallback, useRef } from 'react';
import { RotateCcw, Trophy, Undo2, AlertCircle, Check, ChevronDown, ChevronUp, Radio } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { tennisGameAPI } from './services/tennisGameApi';
import ConfirmModal from '../../components/ConfirmModal';
// Tennis point values display
const POINT_DISPLAY = {
  0: '0',
  1: '15',
  2: '30',
  3: '40'
};

const POLL_INTERVAL_MS = 5000;

/**
 * TennisScoring Component
 *
 * A scoring component for Tennis matches.
 * Follows the modular flow pattern where GameComponent handles
 * settings and team formation. This component only handles scoring
 * and completed phases.
 *
 * For doubles, serving order selection is shown within scoring
 * if not yet set (similar to PingPong initial serve selection).
 */
const TennisScoring = ({
  // Modular flow props (from GameComponent)
  gameCommonId,
  gameSettings,
  participantAInfo,
  participantBInfo,
  onGameComplete,
  onGamePointsStatus,
  // Event data
  event,
  isScorer = true,
  // Centralized pause state from GameComponent — when true, disable all scoring inputs.
  paused = false
}) => {
  const { theme } = useTheme();

  // Match state from API
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Polling state for real-time sync
  const [isPolling, setIsPolling] = useState(false);
  const pollIntervalRef = useRef(null);
  const lastPointCountRef = useRef(0);

  // Serving order state for doubles (shown at start of scoring if not set)
  const [servingOrderSet, setServingOrderSet] = useState(false);
  const [servingOrder, setServingOrder] = useState({
    team1: [0, 1],
    team2: [0, 1],
  });
  const [initialServingTeam, setInitialServingTeam] = useState('A');

  // Player statistics (local cache from API)
  const [playerStats, setPlayerStats] = useState({});

  // UI state
  const [confirmModal, setConfirmModal] = useState({ isOpen: false });
  const [showStats, setShowStats] = useState(false);

  // Detailed scoring state
  const [detailedScoring, setDetailedScoring] = useState(false);
  const [pendingWinningTeam, setPendingWinningTeam] = useState(null);
  const [scoredByPlayerId, setScoredByPlayerId] = useState(null);
  const [selectedShotType, setSelectedShotType] = useState(null);
  const [errorByPlayerId, setErrorByPlayerId] = useState(null);

  // Tennis point-ending classifications, three logical groups:
  // - winner: rally winners (Ace gates on serving-team)
  // - error:  rally errors by the opponent (Unforced / Forced)
  // - fault:  service-related point-enders (server auto-loses, error attributed to current server)
  // Labels kept short for phone screens; "Winner" / "Error" / "Fault" implied by the section header.
  const TENNIS_SHOT_TYPES = [
    { key: 'ace',            label: 'Ace',            category: 'winner', color: { active: 'bg-green-700 text-white ring-2 ring-green-300', idle: 'bg-green-100 text-green-800 hover:bg-green-200' } },
    { key: 'forehand_winner', label: 'Forehand',       category: 'winner', color: { active: 'bg-green-700 text-white ring-2 ring-green-300', idle: 'bg-green-100 text-green-800 hover:bg-green-200' } },
    { key: 'backhand_winner', label: 'Backhand',       category: 'winner', color: { active: 'bg-green-700 text-white ring-2 ring-green-300', idle: 'bg-green-100 text-green-800 hover:bg-green-200' } },
    { key: 'volley_winner',  label: 'Volley',         category: 'winner', color: { active: 'bg-green-700 text-white ring-2 ring-green-300', idle: 'bg-green-100 text-green-800 hover:bg-green-200' } },
    { key: 'smash',          label: 'Smash',          category: 'winner', color: { active: 'bg-green-700 text-white ring-2 ring-green-300', idle: 'bg-green-100 text-green-800 hover:bg-green-200' } },
    { key: 'lob_winner',     label: 'Lob',            category: 'winner', color: { active: 'bg-green-700 text-white ring-2 ring-green-300', idle: 'bg-green-100 text-green-800 hover:bg-green-200' } },
    { key: 'unforced_error', label: 'Unforced',       category: 'error',  color: { active: 'bg-amber-600 text-white ring-2 ring-amber-300', idle: 'bg-amber-100 text-amber-800 hover:bg-amber-200' } },
    { key: 'forced_error',   label: 'Forced',         category: 'error',  color: { active: 'bg-amber-600 text-white ring-2 ring-amber-300', idle: 'bg-amber-100 text-amber-800 hover:bg-amber-200' } },
    { key: 'double_fault',   label: 'Double Fault',   category: 'fault',  color: { active: 'bg-rose-600 text-white ring-2 ring-rose-300', idle: 'bg-rose-100 text-rose-800 hover:bg-rose-200' } },
    { key: 'foot_fault',     label: 'Foot Fault',     category: 'fault',  color: { active: 'bg-rose-600 text-white ring-2 ring-rose-300', idle: 'bg-rose-100 text-rose-800 hover:bg-rose-200' } },
  ];

  const resetDetailState = () => {
    setPendingWinningTeam(null);
    setScoredByPlayerId(null);
    setSelectedShotType(null);
    setErrorByPlayerId(null);
  };

  // Completion validation state

  // Derived data from props
  const teams = [
    {
      name: participantAInfo?.name || 'Player 1',
      players: participantAInfo?.players || (participantAInfo?.id ? [{ id: participantAInfo.id, name: participantAInfo.name }] : [])
    },
    {
      name: participantBInfo?.name || 'Player 2',
      players: participantBInfo?.players || (participantBInfo?.id ? [{ id: participantBInfo.id, name: participantBInfo.name }] : [])
    }
  ];

  const format = gameSettings?.format || 'singles';
  const bestOf = gameSettings?.bestOf || 3;
  // Check both format and participantsType, and also player count as fallback
  const isDoubles = format === 'doubles' || gameSettings?.participantsType === 'doubles' ||
    (teams[0].players.length > 1 || teams[1].players.length > 1);

  // Helper to build player stats from API response
  const buildPlayerStats = useCallback((statsArray) => {
    if (!statsArray) return;
    const stats = {};
    statsArray.forEach(s => {
      stats[s.playerId] = {
        name: s.playerName,
        team: s.team,
        pointsWon: s.pointsWon,
        pointsWonOnServe: s.pointsWonOnServe,
        pointsWonOnReturn: s.pointsWonOnReturn,
        aces: s.aces,
        firstServeFaults: s.firstServeFaults,
        doubleFaults: s.doubleFaults,
        winners: s.winners,
        unforcedErrors: s.unforcedErrors,
        forcedErrors: s.forcedErrors,
        gamesWon: s.gamesWon,
        serviceGamesPlayed: s.serviceGamesPlayed,
        serviceGamesHeld: s.serviceGamesHeld,
        breaksOfServe: s.breaksOfServe,
        tiebreakPointsWon: s.tiebreakPointsWon,
      };
    });
    setPlayerStats(stats);
  }, []);

  // Load game data on mount
  useEffect(() => {
    const loadGame = async () => {
      if (!gameCommonId) {
        setLoading(false);
        return;
      }

      try {
        const gameData = await tennisGameAPI.getById(gameCommonId);
        setMatch(gameData);

        // Notify parent whether any points exist
        const hasPoints = (gameData.points?.length || 0) > 0;
        if (onGamePointsStatus) onGamePointsStatus(hasPoints);

        // Pickleball-style flow: we leave the serving-order phase as soon as the
        // first server has been chosen (singles or doubles). For doubles, team B's
        // first server is asked mid-match the first time team B is about to serve,
        // via the needsServerSelection prompt in renderScoringPhase.
        const hasServingTeam = !!gameData.servingTeam;
        const hasPointsAlready = (gameData.points?.length || 0) > 0;
        setServingOrderSet(hasServingTeam || hasPointsAlready);
        if (gameData.team1ServingOrder || gameData.team2ServingOrder) {
          setServingOrder({
            team1: gameData.team1ServingOrder || [0, 1],
            team2: gameData.team2ServingOrder || [0, 1],
          });
        }

        // Build player stats
        buildPlayerStats(gameData.playerStats);

        // Initialize player stats if none exist
        if (!gameData.playerStats || gameData.playerStats.length === 0) {
          const players = [
            ...teams[0].players.map(p => ({ id: p.id, name: p.name, team: 1 })),
            ...teams[1].players.map(p => ({ id: p.id, name: p.name, team: 2 }))
          ];
          if (players.length > 0) {
            await tennisGameAPI.initializePlayerStats(gameCommonId, players);
            // Re-fetch to get the stats
            const refreshed = await tennisGameAPI.getById(gameCommonId);
            setMatch(refreshed);
            buildPlayerStats(refreshed.playerStats);
          }
        }
      } catch (err) {
        console.error('[TennisScoring] Error loading game:', err);
        setError(err.message || 'Failed to load game');
      } finally {
        setLoading(false);
      }
    };

    loadGame();
  }, [gameCommonId]);

  // Polling for real-time sync
  useEffect(() => {
    const pollMatchData = async () => {
      if (!match || match.status === 'completed') return;

      try {
        const refreshedMatch = await tennisGameAPI.getById(gameCommonId);
        const currentPointCount = refreshedMatch.points?.length || 0;

        if (currentPointCount !== lastPointCountRef.current) {
          setMatch(refreshedMatch);
          lastPointCountRef.current = currentPointCount;
          buildPlayerStats(refreshedMatch.playerStats);
        }
      } catch (err) {
        console.error('[TennisScoring] Polling error:', err);
      }
    };

    if (match && match.status !== 'completed') {
      setIsPolling(true);
      lastPointCountRef.current = match.points?.length || 0;
      pollIntervalRef.current = setInterval(pollMatchData, POLL_INTERVAL_MS);
    } else {
      setIsPolling(false);
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [match?.id, match?.status, gameCommonId]);

  // Handle visibility change - refresh when tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && match && match.status !== 'completed') {
        try {
          const refreshedMatch = await tennisGameAPI.getById(gameCommonId);
          setMatch(refreshedMatch);
          lastPointCountRef.current = refreshedMatch.points?.length || 0;
          buildPlayerStats(refreshedMatch.playerStats);
        } catch (err) {
          console.error('[TennisScoring] Visibility refresh error:', err);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [match?.id, match?.status, gameCommonId]);

  // Get current server info
  const getCurrentServer = () => {
    if (!match) return null;
    const servingTeam = match.servingTeam || 'A';
    const servingPlayerIndex = match.servingPlayerIndex || 0;
    const teamIdx = servingTeam === 'A' ? 0 : 1;
    const teamPlayers = teams[teamIdx]?.players || [];

    if (!isDoubles) {
      return teamPlayers[0];
    } else {
      const order = servingTeam === 'A' ? servingOrder.team1 : servingOrder.team2;
      const playerIdx = order[servingPlayerIndex % order.length];
      return teamPlayers[playerIdx];
    }
  };

  // Get point display string
  const getPointDisplay = (team1Points, team2Points, isDeuce, advantageTeam) => {
    if (isDeuce) {
      if (advantageTeam === 'A') return { team1: 'AD', team2: '40' };
      if (advantageTeam === 'B') return { team1: '40', team2: 'AD' };
      return { team1: '40', team2: '40' };
    }
    return {
      team1: POINT_DISPLAY[team1Points] || team1Points.toString(),
      team2: POINT_DISPLAY[team2Points] || team2Points.toString()
    };
  };

  // Get tiebreak point display
  const getTiebreakDisplay = () => {
    return {
      team1: (match?.tiebreakTeam1Points || 0).toString(),
      team2: (match?.tiebreakTeam2Points || 0).toString()
    };
  };

  // Get score display for current game
  const getCurrentGameScore = () => {
    if (!match) return { team1: '0', team2: '0' };
    if (match.isTiebreak) {
      return getTiebreakDisplay();
    }
    return getPointDisplay(
      match.team1Points || 0,
      match.team2Points || 0,
      match.isDeuce || false,
      match.advantageTeam
    );
  };

  // Record a point via API
  const recordPoint = async (winningTeam, winningPlayerId, pointType = 'point', errorPlayerId = null) => {
    setSaving(true);
    setError(null);

    try {
      const currentServer = getCurrentServer();

      // For singles errors (forced or unforced), auto-attribute to the lone losing player.
      let finalErrorPlayerId = errorPlayerId;
      const isErrorPointType = pointType === 'unforced-error' || pointType === 'forced-error';
      if (isErrorPointType && !isDoubles && !errorPlayerId) {
        const losingTeamIdx = winningTeam === 1 ? 1 : 0;
        const losingTeamPlayers = teams[losingTeamIdx]?.players || [];
        finalErrorPlayerId = losingTeamPlayers[0]?.id;
      }

      const result = await tennisGameAPI.recordPoint(match.id, {
        winningTeam, // 1 or 2 - tennisGameApi.js maps to A/B
        winningPlayerId,
        serverPlayerId: currentServer?.id,
        pointType,
        errorPlayerId: finalErrorPlayerId,
        isDoubleFault: pointType === 'double-fault',
        isAce: pointType === 'ace',
      });

      setMatch(result.match);
      buildPlayerStats(result.match.playerStats);

      // Notify parent about first point
      if (onGamePointsStatus) onGamePointsStatus(true);
    } catch (err) {
      console.error('[TennisScoring] Error recording point:', err);
      setError(err.message || 'Failed to record point');
    } finally {
      setSaving(false);
    }
  };

  // Map detailed shot types to the backend point_type categories
  const mapShotTypeToPointType = (shotType) => {
    if (!shotType) return 'point';
    if (shotType === 'ace') return 'ace';
    if (shotType === 'double_fault') return 'double-fault';
    // Foot fault → server loses the point; reuse double-fault path for stats parity
    // until the backend grows a dedicated 'foot-fault' point_type.
    if (shotType === 'foot_fault') return 'double-fault';
    if (shotType === 'unforced_error') return 'unforced-error';
    if (shotType === 'forced_error') return 'forced-error';
    if (['forehand_winner', 'backhand_winner', 'volley_winner', 'smash', 'lob_winner'].includes(shotType)) return 'winner';
    return 'point';
  };

  // Handle serve fault
  const handleServeFault = async () => {
    setSaving(true);
    setError(null);

    try {
      const updatedMatch = await tennisGameAPI.recordServeFault(match.id);
      setMatch(updatedMatch);
    } catch (err) {
      console.error('[TennisScoring] Error recording serve fault:', err);
      setError(err.message || 'Failed to record serve fault');
    } finally {
      setSaving(false);
    }
  };

  // Handle undo
  const handleUndo = async () => {
    setSaving(true);
    setError(null);

    try {
      const updatedMatch = await tennisGameAPI.undoLastPoint(match.id);
      setMatch(updatedMatch);
      buildPlayerStats(updatedMatch.playerStats);
    } catch (err) {
      console.error('[TennisScoring] Error undoing point:', err);
      setError(err.message || 'Failed to undo point');
    } finally {
      setSaving(false);
    }
  };

  // Refresh match from backend
  const handleRefresh = async () => {
    setSaving(true);
    setError(null);
    try {
      const refreshed = await tennisGameAPI.getById(gameCommonId);
      setMatch(refreshed);
      if (refreshed.playerStats) buildPlayerStats(refreshed.playerStats);
    } catch (err) {
      console.error('[TennisScoring] Failed to refresh:', err);
      setError('Failed to refresh. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Reset match
  const handleResetMatch = () => {
    setConfirmModal({
      isOpen: true,
      title: 'Reset Match',
      message: 'Are you sure you want to reset this match? All progress will be lost.',
      confirmText: 'Reset',
      onConfirm: async () => {
        setConfirmModal({ isOpen: false });
        setSaving(true);
        try {
          const updatedMatch = await tennisGameAPI.reset(match.id);
          setMatch(updatedMatch);

          // Reset local stats
          const stats = {};
          [...teams[0].players, ...teams[1].players].forEach(player => {
            stats[player.id] = {
              name: player.name,
              team: teams[0].players.some(p => p.id === player.id) ? 'A' : 'B',
              pointsWon: 0, pointsWonOnServe: 0, pointsWonOnReturn: 0,
              aces: 0, firstServeFaults: 0, doubleFaults: 0,
              winners: 0, unforcedErrors: 0, forcedErrors: 0, gamesWon: 0,
              serviceGamesPlayed: 0, serviceGamesHeld: 0,
              breaksOfServe: 0, tiebreakPointsWon: 0,
            };
          });
          setPlayerStats(stats);
        } catch (err) {
          console.error('[TennisScoring] Error resetting match:', err);
          setError(err.message || 'Failed to reset match');
        } finally {
          setSaving(false);
        }
      },
      onCancel: () => setConfirmModal({ isOpen: false })
    });
  };

  // Pickleball-style: no modal. Singles auto-records. Doubles stages
  // pendingWinningTeam so an inline player picker renders below the score.
  const showPointAttribution = (winningTeam, pointType) => {
    if (!isDoubles) {
      const teamIdx = winningTeam - 1;
      const winningTeamPlayers = teams[teamIdx]?.players || [];
      const winningPlayerId = winningTeamPlayers[0]?.id;
      recordPoint(winningTeam, winningPlayerId, pointType);
      return;
    }
    setPendingWinningTeam(winningTeam);
    setScoredByPlayerId(null);
    setSelectedShotType(null);
    setErrorByPlayerId(null);
  };

  // Pickleball-style single-tap serve selection.
  // Used both for the initial "Who is serving first?" card and for the
  // mid-game prompt that fires the first time team B is about to serve.
  const handleServeSelected = useCallback(async (team, playerId) => {
    setSaving(true);
    setError(null);
    try {
      const refreshed = await tennisGameAPI.setInitialServe(match.id, {
        servingTeam: team,
        servingPlayerId: playerId,
      });
      setMatch(refreshed);
      buildPlayerStats(refreshed.playerStats);
      setServingOrderSet(true);
    } catch (err) {
      console.error('[TennisScoring] Error setting initial serve:', err);
      setError(err.message || 'Failed to set initial serve');
    } finally {
      setSaving(false);
    }
  }, [match?.id, gameCommonId, buildPlayerStats]);

  // Complete event with validation
  const handleCompleteEvent = async () => {
    if (!match) return;

    // Build result object matching PingPong pattern
    const winnerTeam = match.winnerTeam;
    const winnerInfo = winnerTeam === 'A' ? participantAInfo : participantBInfo;
    const result = {
      winnerId: winnerInfo?.id,
      winnerType: winnerInfo?.type || 'user',
      scoreSummary: match.scoreSummary || `${match.team1Sets}-${match.team2Sets}`,
      playerAScore: match.team1Sets,
      playerBScore: match.team2Sets,
      winner: winnerInfo,
      gameId: match.id
    };

    if (onGameComplete) {
      await onGameComplete(result);
    }
  };

  // Generate scorecard text for sharing

  // ─── Render: Serving Order (pickleball-style) ────────────────────
  // Single tap on a player chooses the first server (and that team's serving
  // order). For doubles, team B's first server is asked mid-match the first
  // time it's about to serve — see the mid-game prompt in renderScoringPhase.

  const renderServingOrderPhase = () => {
    if (!isScorer) {
      return (
        <div className={`${theme.card} rounded-lg border ${theme.border} p-6 text-center`}>
          <p className={`${theme.textMuted}`}>Waiting for scorer to set serving order...</p>
        </div>
      );
    }

    const team1Players = teams[0].players;
    const team2Players = teams[1].players;

    return (
      <div className={`${theme.card} rounded-lg border ${theme.border} p-4 space-y-4`}>
        <h3 className={`${theme.text} font-semibold text-center`}>Who is serving first?</h3>
        {isDoubles ? (
          <div className="grid grid-cols-2 gap-4">
            {/* Team 1 column */}
            <div className="flex flex-col gap-3">
              <div className="text-xs font-semibold text-center uppercase tracking-wide text-green-500 truncate">
                {teams[0].name}
              </div>
              {team1Players.map(player => {
                const firstName = player.name?.split(' ')[0] || player.name || '';
                return (
                  <button
                    key={player.id}
                    onClick={() => handleServeSelected(1, player.id)}
                    disabled={saving || paused}
                    className="w-full bg-green-600 hover:bg-green-700 active:bg-green-800 active:scale-95 text-white py-5 rounded-lg font-semibold transition-transform disabled:opacity-50"
                  >
                    <span className="sm:hidden">{firstName.slice(0, 3)}</span>
                    <span className="hidden sm:inline">{firstName}</span>
                  </button>
                );
              })}
            </div>
            {/* Team 2 column */}
            <div className="flex flex-col gap-3">
              <div className="text-xs font-semibold text-center uppercase tracking-wide text-blue-400 truncate">
                {teams[1].name}
              </div>
              {team2Players.map(player => {
                const firstName = player.name?.split(' ')[0] || player.name || '';
                return (
                  <button
                    key={player.id}
                    onClick={() => handleServeSelected(2, player.id)}
                    disabled={saving || paused}
                    className="w-full bg-blue-600 hover:bg-blue-700 active:bg-blue-800 active:scale-95 text-white py-5 rounded-lg font-semibold transition-transform disabled:opacity-50"
                  >
                    <span className="sm:hidden">{firstName.slice(0, 3)}</span>
                    <span className="hidden sm:inline">{firstName}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => handleServeSelected(1, team1Players?.[0]?.id)}
              disabled={saving || paused}
              className="py-4 px-4 rounded-lg font-semibold text-lg transition-colors bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
            >
              {teams[0].name}
            </button>
            <button
              onClick={() => handleServeSelected(2, team2Players?.[0]?.id)}
              disabled={saving || paused}
              className="py-4 px-4 rounded-lg font-semibold text-lg transition-colors bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
            >
              {teams[1].name}
            </button>
          </div>
        )}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}
      </div>
    );
  };

  // ─── Render: Scoring Phase ────────────────────────────────────────────────

  const renderScoringPhase = () => {
    if (!match) return null;

    const currentServer = getCurrentServer();
    const gameScore = getCurrentGameScore();
    const servingTeamNum = (match.servingTeam || 'A') === 'A' ? 1 : 2;

    // Mid-game server selection: doubles only, when the current serving team
    // has no serving order yet (e.g. team B's first service game has arrived
    // and we only asked team A upfront).
    const currentServingTeam = match.servingTeam === 'A' ? 1 : 2;
    const currentTeamOrder = currentServingTeam === 1 ? match.team1ServingOrder : match.team2ServingOrder;
    const needsServerSelection = isDoubles && !currentTeamOrder;

    return (
      <div className="space-y-4">
        {/* Polling indicator — paused state is shown by the centralized PausedBanner */}
        {isPolling && match.status !== 'paused' && (
          <div className="flex items-center justify-end gap-1 text-green-500 text-xs">
            <Radio className="w-3 h-3 animate-pulse" />
            Live sync
          </div>
        )}

        {/* Mid-game server selection (doubles only) — pickleball-style prompt */}
        {isScorer && needsServerSelection && (
          <div className={`${theme.card} rounded-lg border ${theme.border} p-4 space-y-3`}>
            <h3 className={`${theme.text} font-semibold text-center`}>
              Who is serving for {teams[currentServingTeam - 1]?.name}?
            </h3>
            <div className="flex flex-col gap-2">
              {(teams[currentServingTeam - 1]?.players || []).map(player => {
                const firstName = player.name?.split(' ')[0] || player.name || '';
                return (
                  <button
                    key={player.id}
                    onClick={() => handleServeSelected(currentServingTeam, player.id)}
                    disabled={saving || paused}
                    className={`w-full py-5 px-4 rounded-lg font-semibold transition-transform active:scale-95 ${
                      currentServingTeam === 1
                        ? 'bg-green-600 hover:bg-green-700 active:bg-green-800'
                        : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
                    } text-white disabled:opacity-50`}
                  >
                    <span className="sm:hidden">{firstName.slice(0, 3)}</span>
                    <span className="hidden sm:inline">{firstName}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {!isScorer && needsServerSelection && (
          <div className={`${theme.card} rounded-lg border ${theme.border} p-6 text-center`}>
            <p className={`${theme.textMuted}`}>Waiting for scorer to select server...</p>
          </div>
        )}

        {/* Match Header */}
        <div className={`${theme.card} rounded-lg border p-4`}>
          <div className="flex items-center justify-between mb-2">
            <div className={`text-sm ${theme.textMuted}`}>
              Set {match.currentSet || 1} of {bestOf}
              {match.isTiebreak && ' - Tiebreak'}
            </div>
            <button
              onClick={handleRefresh}
              disabled={saving}
              className={`${theme.textMuted} hover:text-blue-500 flex items-center gap-1 text-sm disabled:opacity-50`}
              title="Refresh from server"
            >
              <RotateCcw className={`w-4 h-4 ${saving ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {/* Sets Score */}
          <div className={`text-center text-lg font-semibold ${theme.text} mb-2`}>
            {teams[0].name} {match.team1Sets || 0} - {match.team2Sets || 0} {teams[1].name}
          </div>

          {/* Set History */}
          {(match.setResults || []).length > 0 && (
            <div className={`text-center text-sm ${theme.textMuted}`}>
              {(match.setResults || []).map((set, idx) => (
                <span key={idx} className="mr-2">
                  {set.team1Games}-{set.team2Games}
                  {set.wasTiebreak && <sup>({set.tiebreakTeam1Points}-{set.tiebreakTeam2Points})</sup>}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Games Score */}
        <div className={`${theme.card} rounded-lg border p-4`}>
          <div className={`text-center text-sm ${theme.textMuted} mb-2`}>Games</div>
          <div className="flex justify-center items-center gap-8">
            <div className={`text-4xl font-bold ${servingTeamNum === 1 ? 'text-green-600' : theme.text}`}>
              {match.team1Games || 0}
            </div>
            <div className={`text-2xl ${theme.textMuted}`}>-</div>
            <div className={`text-4xl font-bold ${servingTeamNum === 2 ? 'text-green-600' : theme.text}`}>
              {match.team2Games || 0}
            </div>
          </div>
        </div>

        {/* Current Game/Tiebreak Score — tappable in both modes; in detailed mode it stages the winning team */}
        <div className="grid grid-cols-2 gap-4">
          {[1, 2].map(teamNum => {
            const score = teamNum === 1 ? gameScore.team1 : gameScore.team2;
            const isServing = servingTeamNum === teamNum;
            const tappable = isScorer && !saving && !paused;
            const isPending = detailedScoring && pendingWinningTeam === teamNum;
            const onTeamTap = () => {
              if (!tappable) return;
              if (detailedScoring) {
                setPendingWinningTeam(teamNum);
                setSelectedShotType(null);
                setErrorByPlayerId(null);
                if (!isDoubles) {
                  setScoredByPlayerId(teams[teamNum - 1]?.players?.[0]?.id || null);
                } else {
                  // Auto-default the winning player so a winner-shot tap submits immediately.
                  // Suggested default: serving team → current server; otherwise first team player.
                  // The "Who won the point?" picker still shows so the scorer can override before tapping a shot.
                  const suggested = teamNum === servingTeamNum
                    ? getCurrentServer()?.id
                    : teams[teamNum - 1]?.players?.[0]?.id;
                  setScoredByPlayerId(suggested || null);
                }
              } else {
                showPointAttribution(teamNum, 'point');
              }
            };
            return (
              <button
                key={teamNum}
                type="button"
                onClick={onTeamTap}
                disabled={!tappable}
                className={`${theme.card} rounded-lg border p-6 text-center transition-all ${
                  isServing ? 'ring-2 ring-red-500 ring-offset-1' : ''
                } ${isPending ? 'ring-2 ring-yellow-400 ring-offset-1' : ''} ${tappable ? `active:scale-95 ${theme.cardHover} cursor-pointer` : 'cursor-default'}`}
              >
                <div className={`text-sm ${theme.textMuted} mb-2`}>{teams[teamNum - 1].name}</div>
                <div className={`text-5xl font-bold ${theme.text} mb-2`}>{score}</div>
                {isServing && (
                  <div className="text-red-600 text-sm font-medium">
                    SERVING {(match.serveAttempt || 1) === 2 ? '(2nd)' : ''}
                  </div>
                )}
              </button>
            );
          })}
        </div>
        <p className={`text-center text-sm ${isScorer ? 'visible' : 'invisible'} ${
          detailedScoring && !pendingWinningTeam ? 'text-amber-600 font-medium' : theme.textMuted
        }`}>
          {detailedScoring
            ? (pendingWinningTeam
                ? 'Now choose a shot type below'
                : 'Tap a team\'s score first (Ace works without it)')
            : 'Tap a team\'s score to add a point'}
        </p>

        {/* Inline player picker for SIMPLE mode (doubles only). Replaces the
            old "Who won the point?" modal. Singles records immediately on tap. */}
        {isScorer && !detailedScoring && isDoubles && pendingWinningTeam != null && (
          <div className={`${theme.card} rounded-lg border ${theme.border} p-4 space-y-3`}>
            <div className="flex items-center justify-between">
              <h3 className={`${theme.text} font-semibold`}>
                {teams[pendingWinningTeam - 1]?.name} — Who won the point?
              </h3>
              <button onClick={resetDetailState} className={`text-sm ${theme.textMuted} hover:${theme.text}`}>
                Cancel
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {(teams[pendingWinningTeam - 1]?.players || []).map(player => {
                const isServer = currentServer && String(currentServer.id) === String(player.id);
                const firstName = player.name?.split(' ')[0] || player.name || '';
                return (
                  <button
                    key={player.id}
                    onClick={() => {
                      recordPoint(pendingWinningTeam, player.id, 'point');
                      resetDetailState();
                    }}
                    disabled={saving || paused}
                    className={`py-4 px-3 rounded-lg font-semibold transition-transform active:scale-95 text-white disabled:opacity-50 ${
                      pendingWinningTeam === 1
                        ? 'bg-green-600 hover:bg-green-700 active:bg-green-800'
                        : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
                    } ${isServer ? 'ring-2 ring-red-500 ring-offset-1' : ''}`}
                  >
                    <span className="sm:hidden">{firstName.slice(0, 3)}</span>
                    <span className="hidden sm:inline">{firstName}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Server Info */}
        <div className={`${theme.card} rounded-lg border p-3`}>
          <div className={`text-center ${theme.text}`}>
            <span className={theme.textMuted}>Server: </span>
            <span className="font-semibold">{currentServer?.name}</span>
            <span className={theme.textMuted}> | </span>
            <span className={theme.textMuted}>{(match.serveSide || 'deuce') === 'deuce' ? 'Deuce' : 'Ad'} Court</span>
            {(match.serveAttempt || 1) === 2 && (
              <span className="ml-2 text-yellow-600 font-medium">Second Serve</span>
            )}
          </div>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}

        {/* Detailed Scoring Toggle */}
        {isScorer && (
          <label className={`flex items-center gap-2 text-sm ${theme.textMuted} cursor-pointer select-none`}>
            <input
              type="checkbox"
              checked={detailedScoring}
              onChange={e => { setDetailedScoring(e.target.checked); resetDetailState(); }}
              className="rounded"
            />
            <span>Enable Detailed Scoring</span>
          </label>
        )}

        {/* First-serve Fault — separate quick action (doesn't end the point in tennis) */}
        {isScorer && detailedScoring && (
          <button
            onClick={handleServeFault}
            disabled={saving || (match.serveAttempt || 1) === 2 || paused}
            className="w-full bg-yellow-500 hover:bg-yellow-600 text-white py-3 rounded-lg font-semibold disabled:opacity-50"
          >
            {(match.serveAttempt || 1) === 2 ? '2nd Serve in progress' : 'First-Serve Fault'}
          </button>
        )}

        {/* Detail scoring panel — always visible when detailedScoring is on. Shot-type click auto-submits.
            Three logical groups: Winner / Opponent Error / Service Fault.
            Mobile-first sizing: 2-col grid on phones, 3-col at sm+. py-3 for >=48px touch target. */}
        {isScorer && detailedScoring && (() => {
          const winners = TENNIS_SHOT_TYPES.filter(s => s.category === 'winner');
          const opponentErrors = TENNIS_SHOT_TYPES.filter(s => s.category === 'error');
          const serviceFaults = TENNIS_SHOT_TYPES.filter(s => s.category === 'fault');

          // Auto-submit helper for any winner shot.
          // Ace is special: it auto-resolves to the serving team + current server.
          // Other winners require pendingWinningTeam (set by tapping a team's score).
          const submitWinner = (shot) => {
            const isAce = shot.key === 'ace';
            const winningTeam = isAce ? servingTeamNum : pendingWinningTeam;
            if (winningTeam == null) return;
            const winningPlayers = teams[winningTeam - 1]?.players || [];
            const playerId = isAce
              ? getCurrentServer()?.id
              : (!isDoubles ? winningPlayers[0]?.id : scoredByPlayerId);
            if (isDoubles && !isAce && !playerId) {
              // doubles winner needs a scored-by player first — stage and wait for player tap
              setSelectedShotType(shot.key);
              return;
            }
            recordPoint(winningTeam, playerId, mapShotTypeToPointType(shot.key));
            resetDetailState();
          };

          // Rally-error handler (Unforced / Forced). Singles auto-submits with sole loser as
          // errorPlayerId. Doubles stages the shot and waits for "who made the error?" tap.
          const submitOrStageError = (shot) => {
            const winningTeam = pendingWinningTeam;
            if (winningTeam == null) return;
            if (!isDoubles) {
              const winnerPlayers = teams[winningTeam - 1]?.players || [];
              recordPoint(winningTeam, winnerPlayers[0]?.id, mapShotTypeToPointType(shot.key));
              resetDetailState();
            } else {
              setSelectedShotType(shot.key);
              setErrorByPlayerId(null);
            }
          };

          // Service-fault handler (Double Fault, Foot Fault). Always auto-resolves:
          // server loses, opponent wins, error attributed to current server. No follow-up tap.
          const submitFault = (shot) => {
            const winningTeam = servingTeamNum === 1 ? 2 : 1;
            const winnerPlayers = teams[winningTeam - 1]?.players || [];
            const winnerPlayerId = !isDoubles ? winnerPlayers[0]?.id : (scoredByPlayerId || winnerPlayers[0]?.id);
            recordPoint(winningTeam, winnerPlayerId, mapShotTypeToPointType(shot.key), getCurrentServer()?.id);
            resetDetailState();
          };

          // Shared button class. Adequate touch target for iOS HIG (44pt) and Material (48dp).
          const btnCls = (shot, disabled) =>
            `py-3 px-2 rounded-lg text-sm font-semibold transition-all active:scale-95 ${
              selectedShotType === shot.key
                ? shot.color.active
                : disabled
                  ? 'opacity-40 cursor-not-allowed bg-gray-100 text-gray-400'
                  : shot.color.idle
            }`;

          return (
            <div className={`${theme.card} rounded-lg border ${theme.border} p-4 space-y-4`}>
              {/* Header — only when a team has been tapped */}
              {pendingWinningTeam != null && (
                <div className="flex items-center justify-between">
                  <h3 className={`${theme.text} font-semibold`}>
                    {(() => {
                      const players = teams[pendingWinningTeam - 1]?.players || [];
                      const scorer = scoredByPlayerId ? players.find(p => String(p.id) === String(scoredByPlayerId)) : null;
                      return scorer?.name || teams[pendingWinningTeam - 1]?.name;
                    })()} Won Point
                  </h3>
                  <button onClick={resetDetailState} className={`text-sm ${theme.textMuted} hover:${theme.text}`}>
                    Cancel
                  </button>
                </div>
              )}

              {/* Doubles only: who won the point? Stays visible after team-tap so the scorer can
                  override the auto-defaulted player before tapping a winner shot. Hidden once an
                  error shot is staged (then the "Who made the error?" picker takes over). */}
              {isDoubles && pendingWinningTeam != null && !['forced_error', 'unforced_error'].includes(selectedShotType) && (
                <div>
                  <div className={`text-sm font-medium ${theme.textMuted} mb-2`}>Who won the point? <span className="font-normal">(default selected — tap to change)</span></div>
                  <div className="grid grid-cols-2 gap-2">
                    {(teams[pendingWinningTeam - 1]?.players || []).map(player => {
                      const currentServer = getCurrentServer();
                      const isServer = currentServer && String(currentServer.id) === String(player.id);
                      return (
                        <button
                          key={player.id}
                          onClick={() => { setScoredByPlayerId(player.id); setSelectedShotType(null); setErrorByPlayerId(null); }}
                          className={`py-3 px-3 rounded-lg text-sm font-medium transition-colors ${
                            String(scoredByPlayerId) === String(player.id)
                              ? 'bg-green-600 text-white'
                              : `${theme.secondary} ${theme.secondaryHover} ${theme.text}`
                          } ${isServer ? 'ring-2 ring-red-500 ring-offset-1' : ''}`}
                        >
                          {player.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Group 1: WINNER — green tones. Ace enabled even without a pending team. */}
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-green-700 mb-1.5">Winner</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {winners.map(shot => {
                    const isAce = shot.key === 'ace';
                    const aceConflict = isAce && pendingWinningTeam != null && pendingWinningTeam !== servingTeamNum;
                    const disabled = aceConflict || (!isAce && pendingWinningTeam == null);
                    return (
                      <button
                        key={shot.key}
                        onClick={() => submitWinner(shot)}
                        disabled={disabled || saving || paused}
                        className={btnCls(shot, disabled)}
                      >
                        {shot.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Group 2: OPPONENT ERROR — amber tones. Requires a pending team.
                  The "Who made the error?" picker appears right below this group (doubles only)
                  so the eye doesn't have to jump down past Service Fault to find the next tap. */}
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-amber-700 mb-1.5">Opponent Error</div>
                <div className="grid grid-cols-2 gap-2">
                  {opponentErrors.map(shot => {
                    const disabled = pendingWinningTeam == null;
                    return (
                      <button
                        key={shot.key}
                        onClick={() => submitOrStageError(shot)}
                        disabled={disabled || saving || paused}
                        className={btnCls(shot, disabled)}
                      >
                        {shot.label}
                      </button>
                    );
                  })}
                </div>

                {/* Doubles + error shot: pick who made the error → submits on tap.
                    Lives inside the Opponent Error group so the next tap is right under the previous one. */}
                {isDoubles && pendingWinningTeam != null && selectedShotType && (selectedShotType === 'forced_error' || selectedShotType === 'unforced_error') && (
                  <div className="mt-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                    <div className={`text-sm font-medium ${theme.text} mb-2`}>Who made the error?</div>
                    <div className="grid grid-cols-2 gap-2">
                      {(teams[pendingWinningTeam === 1 ? 1 : 0]?.players || []).map(player => (
                        <button
                          key={player.id}
                          onClick={() => {
                            const winnerPlayers = teams[pendingWinningTeam - 1]?.players || [];
                            const winnerPlayerId = scoredByPlayerId || winnerPlayers[0]?.id;
                            recordPoint(pendingWinningTeam, winnerPlayerId, mapShotTypeToPointType(selectedShotType), player.id);
                            resetDetailState();
                          }}
                          disabled={saving || paused}
                          className="py-3 px-3 rounded-lg text-sm font-medium transition-colors active:scale-95 bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50"
                        >
                          {player.name}
                          <span className="block text-xs opacity-80">
                            {pendingWinningTeam === 1 ? teams[1]?.name : teams[0]?.name}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Group 3: SERVICE FAULT — rose tones. Always enabled (auto-resolves: server loses). */}
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-rose-700 mb-1.5">
                  Service Fault <span className={`font-normal normal-case ${theme.textMuted}`}>· server loses</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {serviceFaults.map(shot => {
                    // Faults are always enabled — they auto-resolve to opponent winning.
                    // If the user happens to have staged the serving team as the winner, that's ignored.
                    return (
                      <button
                        key={shot.key}
                        onClick={() => submitFault(shot)}
                        disabled={saving || paused}
                        className={btnCls(shot, false)}
                      >
                        {shot.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Undo + Reset row */}
        {isScorer && !pendingWinningTeam && match.status !== 'paused' && (
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={handleUndo}
              disabled={saving || !(match.points && match.points.length > 0) || paused}
              className={`${theme.secondary} ${theme.secondaryHover} py-3 rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50`}
            >
              <Undo2 className="w-4 h-4" />
              Undo
            </button>
            <button
              onClick={handleResetMatch}
              disabled={saving || paused}
              className="py-3 rounded-lg font-medium flex items-center justify-center gap-2 bg-rose-100 text-rose-800 hover:bg-rose-200 dark:bg-rose-900/30 dark:text-rose-300 disabled:opacity-50"
            >
              <RotateCcw className="w-4 h-4" />
              Reset Match
            </button>
          </div>
        )}

        {/* Statistics (Collapsible) */}
        <div className={`${theme.card} rounded-lg border`}>
          <button
            onClick={() => setShowStats(!showStats)}
            className={`w-full p-4 flex items-center justify-between ${theme.text} font-medium`}
          >
            <span>Player Statistics</span>
            {showStats ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>

          {showStats && (
            <div className="p-4 pt-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className={theme.textMuted}>
                    <th className="text-left py-2">Player</th>
                    <th className="text-center py-2">Pts</th>
                    <th className="text-center py-2">Aces</th>
                    <th className="text-center py-2">DF</th>
                    <th className="text-center py-2">Win</th>
                    <th className="text-center py-2" title="Unforced Errors">UE</th>
                    <th className="text-center py-2" title="Forced Errors">FE</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.values(playerStats).map((stat) => (
                    <tr key={stat.name} className={`${theme.text} border-t ${theme.border}`}>
                      <td className="py-2">{stat.name}</td>
                      <td className="text-center py-2">{stat.pointsWon}</td>
                      <td className="text-center py-2">{stat.aces}</td>
                      <td className="text-center py-2">{stat.doubleFaults}</td>
                      <td className="text-center py-2">{stat.winners}</td>
                      <td className="text-center py-2">{stat.unforcedErrors}</td>
                      <td className="text-center py-2">{stat.forcedErrors ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Point History (Rally History) */}
        {(match.points?.length || 0) > 0 && (
          <details className={`${theme.card} rounded-lg border ${theme.border}`}>
            <summary className={`${theme.text} p-4 cursor-pointer font-medium`}>
              Point History ({match.points.length} points)
            </summary>
            <div className="p-4 pt-0 max-h-48 overflow-y-auto">
              {[...(match.points || [])].reverse().slice(0, 15).map((point, idx) => {
                const allPlayers = [...(teams[0].players || []), ...(teams[1].players || [])];
                const findPlayer = (id) => allPlayers.find(p => String(p.id) === String(id))?.name;
                const winnerPlayer = point.winningPlayerId ? findPlayer(point.winningPlayerId) : null;
                const errorPlayer = point.errorPlayerId ? findPlayer(point.errorPlayerId) : null;
                const serverPlayer = point.serverPlayerId ? findPlayer(point.serverPlayerId) : null;
                const isTeamA = point.winningTeam === 'A';
                const winnerTeamName = isTeamA ? teams[0].name : teams[1].name;
                const isErrorType = point.pointType === 'unforced-error' || point.pointType === 'forced-error';

                // Format score before point (tennis notation)
                const scoreBefore = point.isTiebreak
                  ? `TB ${point.team1PointsBefore}-${point.team2PointsBefore}`
                  : `${POINT_DISPLAY[point.team1PointsBefore] ?? point.team1PointsBefore}-${POINT_DISPLAY[point.team2PointsBefore] ?? point.team2PointsBefore}`;
                const gamesBefore = `(${point.team1GamesBefore}-${point.team2GamesBefore})`;

                return (
                  <div key={point.id} className={`text-sm py-1.5 border-b ${theme.border} last:border-0`}>
                    <div>
                      <span className={theme.textMuted}>#{match.points.length - idx}: </span>
                      <span className={point.isDoubleFault ? 'text-orange-500' : point.isFirstServeFault ? 'text-yellow-600' : isTeamA ? 'text-green-600' : 'text-blue-600'}>
                        {point.isDoubleFault
                          ? 'Double Fault'
                          : point.isFirstServeFault
                            ? 'Serve Fault'
                            : `${winnerPlayer || winnerTeamName} won`}
                      </span>
                      {point.isDoubleFault && serverPlayer && (
                        <span className={theme.textMuted}> by {serverPlayer}</span>
                      )}
                      {!point.isDoubleFault && !point.isFirstServeFault && isErrorType && errorPlayer && (
                        <span className={theme.textMuted}> — {point.pointType.replace(/[-_]/g, ' ')} by {errorPlayer}</span>
                      )}
                      {!point.isDoubleFault && !point.isFirstServeFault && !isErrorType && point.pointType && point.pointType !== 'point' && (
                        <span className={theme.textMuted}> ({point.pointType.replace(/[-_]/g, ' ')})</span>
                      )}
                      <span className={`${theme.textMuted} ml-1`}>Set {point.setNumber} {gamesBefore} {scoreBefore}</span>
                    </div>
                    {serverPlayer && !point.isDoubleFault && (
                      <div className={`text-xs ${theme.textMuted}`}>serve: {serverPlayer}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </details>
        )}
      </div>
    );
  };

  // ─── Render: Completed Phase ──────────────────────────────────────────────

  const renderCompletedPhase = () => {
    if (!match) return null;
    const winnerName = match.winnerTeam === 'A' ? teams[0].name : teams[1].name;

    return (
      <div className="space-y-6">
        {/* Winner Banner */}
        <div className={`${theme.card} rounded-lg border p-6 text-center`}>
          <Trophy className="w-16 h-16 mx-auto text-yellow-500 mb-4" />
          <h2 className={`text-2xl font-bold ${theme.text} mb-2`}>{winnerName} Wins!</h2>
          <div className={theme.textMuted}>
            {match.team1Sets} - {match.team2Sets}
          </div>
        </div>

        {/* Set Results */}
        <div className={`${theme.card} rounded-lg border p-4`}>
          <h3 className={`${theme.text} font-semibold mb-4`}>Set-by-Set Results</h3>
          <div className="space-y-2">
            {(match.setResults || []).map((set, idx) => (
              <div
                key={idx}
                className={`flex justify-between items-center p-3 rounded-lg ${theme.backgroundSecondary}`}
              >
                <span className={theme.text}>Set {idx + 1}</span>
                <span className={`font-semibold ${theme.text}`}>
                  {set.team1Games} - {set.team2Games}
                  {set.wasTiebreak && (
                    <span className={`text-sm ${theme.textMuted} ml-1`}>
                      ({set.tiebreakTeam1Points}-{set.tiebreakTeam2Points})
                    </span>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Player Statistics */}
        <div className={`${theme.card} rounded-lg border p-4`}>
          <h3 className={`${theme.text} font-semibold mb-4`}>Player Statistics</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={theme.textMuted}>
                  <th className="text-left py-2">Player</th>
                  <th className="text-center py-2">Pts</th>
                  <th className="text-center py-2">Aces</th>
                  <th className="text-center py-2">DF</th>
                  <th className="text-center py-2">Win</th>
                  <th className="text-center py-2" title="Unforced Errors">UE</th>
                  <th className="text-center py-2" title="Forced Errors">FE</th>
                  <th className="text-center py-2">Games</th>
                </tr>
              </thead>
              <tbody>
                {Object.values(playerStats).map((stat) => (
                  <tr key={stat.name} className={`${theme.text} border-t ${theme.border}`}>
                    <td className="py-2">{stat.name}</td>
                    <td className="text-center py-2">{stat.pointsWon}</td>
                    <td className="text-center py-2">{stat.aces}</td>
                    <td className="text-center py-2">{stat.doubleFaults}</td>
                    <td className="text-center py-2">{stat.winners}</td>
                    <td className="text-center py-2">{stat.unforcedErrors}</td>
                    <td className="text-center py-2">{stat.forcedErrors ?? 0}</td>
                    <td className="text-center py-2">{stat.gamesWon}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Point History (Set-by-Set) */}
        {(match.points?.length || 0) > 0 && (
          <div className={`${theme.card} rounded-lg border ${theme.border} p-4`}>
            <h3 className={`${theme.text} font-semibold mb-4`}>
              Point History ({match.points.length} total points)
            </h3>

            <div className="space-y-3">
              {(match.setResults || []).map((set, setIdx) => {
                const setPoints = (match.points || []).filter(p => p.setNumber === set.setNumber || p.setNumber === (setIdx + 1));
                if (setPoints.length === 0) return null;

                const setWinnerIsTeamA = set.winnerTeam === 'A';
                const setWinnerName = setWinnerIsTeamA ? teams[0].name : teams[1].name;

                return (
                  <details
                    key={setIdx}
                    className={`${theme.backgroundSecondary} rounded-lg border ${theme.border}`}
                    open={setIdx === (match.setResults.length - 1)}
                  >
                    <summary className="p-3 cursor-pointer flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={`font-medium ${theme.text}`}>Set {setIdx + 1}</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${setWinnerIsTeamA ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                          {setWinnerName} won
                        </span>
                      </div>
                      <div className={`font-semibold ${theme.text}`}>
                        <span className={setWinnerIsTeamA ? 'text-green-600' : ''}>{set.team1Games}</span>
                        <span className={theme.textMuted}> - </span>
                        <span className={!setWinnerIsTeamA ? 'text-green-600' : ''}>{set.team2Games}</span>
                        {set.wasTiebreak && (
                          <span className={`text-sm ${theme.textMuted} ml-1`}>
                            ({set.tiebreakTeam1Points}-{set.tiebreakTeam2Points})
                          </span>
                        )}
                      </div>
                    </summary>

                    <div className="px-3 pb-3">
                      {/* Column headers */}
                      <div className={`grid grid-cols-[2rem_1fr_auto] text-xs ${theme.textMuted} py-1 border-b ${theme.border} mb-1 gap-2`}>
                        <span>#</span>
                        <span>Result</span>
                        <span className="text-right">Score</span>
                      </div>

                      {/* Point rows */}
                      <div className="max-h-60 overflow-y-auto">
                        {setPoints.map((point, idx) => {
                          const isTeamA = point.winningTeam === 'A';
                          const winnerTeamName = isTeamA ? teams[0].name : teams[1].name;

                          const allPlayers = [...(teams[0].players || []), ...(teams[1].players || [])];
                          const findPlayer = (id) => allPlayers.find(p => String(p.id) === String(id))?.name;
                          const winnerPlayer = point.winningPlayerId ? findPlayer(point.winningPlayerId) : null;
                          const errorPlayer = point.errorPlayerId ? findPlayer(point.errorPlayerId) : null;
                          const serverPlayer = point.serverPlayerId ? findPlayer(point.serverPlayerId) : null;
                          const isErrorType = point.pointType === 'unforced-error' || point.pointType === 'forced-error';

                          // Score display
                          const scoreBefore = point.isTiebreak
                            ? `TB ${point.team1PointsBefore}-${point.team2PointsBefore}`
                            : `${POINT_DISPLAY[point.team1PointsBefore] ?? point.team1PointsBefore}-${POINT_DISPLAY[point.team2PointsBefore] ?? point.team2PointsBefore}`;
                          const gamesScore = `G${point.team1GamesBefore}-${point.team2GamesBefore}`;

                          return (
                            <div
                              key={point.id}
                              className={`grid grid-cols-[2rem_1fr_auto] text-xs py-1.5 border-b ${theme.border} last:border-0 gap-2`}
                            >
                              <span className={theme.textMuted}>{idx + 1}</span>
                              <div className="min-w-0">
                                <span className={`font-medium ${
                                  point.isDoubleFault
                                    ? 'text-orange-500'
                                    : point.isFirstServeFault
                                      ? 'text-yellow-600'
                                      : isTeamA ? 'text-green-600' : 'text-blue-600'
                                }`}>
                                  {point.isDoubleFault
                                    ? 'Double Fault'
                                    : point.isFirstServeFault
                                      ? 'Serve Fault'
                                      : (winnerPlayer || winnerTeamName)}
                                </span>
                                {point.isDoubleFault && serverPlayer && (
                                  <span className={theme.textMuted}> by {serverPlayer}</span>
                                )}
                                {!point.isDoubleFault && !point.isFirstServeFault && isErrorType && errorPlayer && (
                                  <span className={theme.textMuted}> — {point.pointType.replace(/[-_]/g, ' ')} by {errorPlayer}</span>
                                )}
                                {!point.isDoubleFault && !point.isFirstServeFault && !isErrorType && point.pointType && point.pointType !== 'point' && (
                                  <span className={`ml-1 ${theme.textMuted}`}>({point.pointType.replace(/[-_]/g, ' ')})</span>
                                )}
                                {serverPlayer && !point.isDoubleFault && (
                                  <div className={`${theme.textMuted} text-[10px]`}>serve: {serverPlayer}</div>
                                )}
                              </div>
                              <span className={`text-right font-medium ${theme.text} whitespace-nowrap`}>
                                {gamesScore} {scoreBefore}
                              </span>
                            </div>
                          );
                        })}
                      </div>

                      {/* Set summary */}
                      <div className={`mt-2 pt-2 border-t ${theme.border} text-xs ${theme.textMuted} flex justify-between`}>
                        <span>{setPoints.length} points played</span>
                        <span>
                          {setPoints.filter(p => p.winningTeam === 'A' && !p.isFirstServeFault).length} - {setPoints.filter(p => p.winningTeam === 'B' && !p.isFirstServeFault).length} points won
                        </span>
                      </div>
                    </div>
                  </details>
                );
              })}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="space-y-3">
          {isScorer && match?.status === 'completed' && event?.status !== 'completed' && (
            <button
              onClick={handleCompleteEvent}
              className={`w-full bg-green-600 hover:bg-green-700 text-white py-4 rounded-lg font-semibold text-lg flex items-center justify-center gap-2`}
            >
              <Check className="w-5 h-5" />
              Complete Event & Continue
            </button>
          )}
        </div>
      </div>
    );
  };

  // ─── Main Render ──────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-green-500 border-t-transparent"></div>
      </div>
    );
  }

  // Determine what to show
  const isCompleted = match?.status === 'completed';
  const needsServingOrder = !servingOrderSet;

  return (
    <div className="space-y-4">


      {/* Show serving order setup if not yet set */}
      {needsServingOrder && !isCompleted && renderServingOrderPhase()}

      {/* Scoring phase */}
      {!needsServingOrder && !isCompleted && renderScoringPhase()}

      {/* Completed phase */}
      {isCompleted && renderCompletedPhase()}

      {confirmModal.isOpen && (
        <ConfirmModal
          isOpen={confirmModal.isOpen}
          title={confirmModal.title}
          message={confirmModal.message}
          confirmText={confirmModal.confirmText}
          onConfirm={confirmModal.onConfirm}
          onClose={confirmModal.onCancel}
        />
      )}
    </div>
  );
};

export default TennisScoring;
